import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  BaseImportPreviewModalComponent,
  BaseImportService,
  ignoreModalError,
  ImportPreview,
  ModalService,
  NotificationService,
} from 'tms-lib';

export interface ImportPreviewModalConfig<TComponent> {
  preview$: Observable<any[]>;
  modalComponent: any;
  modalService: ModalService;
  notificationService?: NotificationService;
  destroy$: Subject<void>;
  onRefresh: () => void;
  onComponentCreated?: (component: TComponent) => void;
  onError?: (error: any) => void;
}

@Injectable({
  providedIn: 'root',
})
export abstract class TmsWebBaseImportService extends BaseImportService {
  protected constructor(baseRoute: string, filename: string) {
    super(baseRoute, filename);
  }

  protected get apiUrl() {
    return `TmsWeb/${this.baseRoute}`;
  }

  preview<T extends ImportPreview>(file: any): Observable<T[]> {
    const formData = this.getFormData(file);
    return this.httpService.post<T[]>(`${this.apiUrl}/Preview`, formData);
  }

  confirmImport<T extends ImportPreview>(rows: T[]): Observable<any> {
    return this.httpService.postForText(`${this.apiUrl}/ImportConfirmed`, rows);
  }

  openImportPreview<TComponent>(config: ImportPreviewModalConfig<TComponent>): void {
    config.preview$.pipe(takeUntil(config.destroy$)).subscribe({
      next: (previewData) => {
        const { modal, component } = config.modalService.openXL<TComponent>(config.modalComponent);
        (component as BaseImportPreviewModalComponent<any>).previewData.set(previewData);
        if (config.onComponentCreated) {
          config.onComponentCreated(component);
        }
        modal.result.then(() => config.onRefresh(), ignoreModalError);
      },
      error: (error: any) => {
        if (config.onError) {
          config.onError(error);
        } else {
          config.notificationService.fail(error?.error || $localize`Failed to preview import file`);
        }
      },
    });
  }
}
